import { ComponentPropsWithoutRef, ReactNode } from 'react'

/**
 * The one place a badge's shape, size, weight and colour are defined.
 *
 * Every badge in the app is a thin wrapper that picks from these closed sets and
 * supplies its own label — so a pill never carries its own ad-hoc class string,
 * and a change to what "pill" or "tiny" means lands everywhere at once. Callers
 * pick semantics (which tone, which size); they don't get to invent styling.
 */

/** rounded-full reads as a chip; rounded-md as a small block or control. */
export type BadgeShape = 'pill' | 'rounded'

/**
 * Named for their type size, since that is what the eye reads first. `smallFlush`
 * is 12px with no vertical padding — for badges sat inline in a row of text that
 * must not add to the row's height.
 */
export type BadgeSize = 'tiny' | 'small' | 'smallFlush' | 'medium'

export type BadgeWeight = 'medium' | 'semibold' | 'bold'

/**
 * Full colour recipes — background, text and any ring or border together, since
 * those only make sense as a set. Ink-scale tones first, then the two specials.
 */
export type BadgeTone =
    | 'solid'
    | 'muted'
    | 'faint'
    | 'neutral'
    | 'outline'
    | 'danger'
    | 'wireframeSolid'
    | 'wireframeMuted'
    | 'wireframeFaint'

const SHAPES: Record<BadgeShape, string> = {
    pill: 'rounded-full',
    rounded: 'rounded-md'
}

const SIZES: Record<BadgeSize, string> = {
    tiny: 'px-1.5 py-0.5 text-[10px]',
    small: 'px-2 py-0.5 text-[11px]',
    smallFlush: 'px-1.5 py-0 text-xs',
    medium: 'px-2.5 py-1 text-xs'
}

const WEIGHTS: Record<BadgeWeight, string> = {
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold'
}

const TONES: Record<BadgeTone, string> = {
    solid: 'bg-ink-800 text-white',
    muted: 'bg-ink-200 text-ink-800',
    faint: 'bg-ink-100 text-ink-600 ring-1 ring-inset ring-ink-200',
    neutral: 'bg-ink-100 text-ink-600',
    outline: 'border border-ink-200 text-ink-500',
    danger: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20',
    // Wireframe-only tones. The entity prototypes were built on Tailwind's gray
    // ramp before the ink scale existed; these keep them pixel-identical rather
    // than quietly restyling a prototype. Delete them when those pages are rebuilt.
    wireframeSolid: 'border border-gray-800 bg-gray-800 text-white',
    wireframeMuted: 'border border-gray-200 bg-gray-100 text-gray-800',
    wireframeFaint: 'border border-gray-200 bg-gray-50 text-gray-700'
}

type Props = {
    tone: BadgeTone
    shape?: BadgeShape
    size?: BadgeSize
    weight?: BadgeWeight
    /** Uppercase with tracking — the eyebrow treatment, for labels that name a kind. */
    caps?: boolean
    children: ReactNode
    /**
     * Layout only: margins and alignment. Colour, padding, radius and type come
     * from a tone/size/shape — reach for a new token rather than overriding here.
     */
    className?: string
} & Omit<ComponentPropsWithoutRef<'span'>, 'className' | 'children'>

export default function Badge({
    tone,
    shape = 'pill',
    size = 'small',
    weight = 'medium',
    caps = false,
    children,
    className = '',
    ...spanProps
}: Props) {
    const classes = [
        'inline-flex items-center',
        SHAPES[shape],
        SIZES[size],
        WEIGHTS[weight],
        caps ? 'uppercase tracking-wide' : '',
        TONES[tone],
        className
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <span className={classes} {...spanProps}>
            {children}
        </span>
    )
}
